import { defineStore } from "pinia";
import type { Pagination } from "@/types";
import api from "@/_helpers/api";
import { axiosInstance } from "@/plugins/https";
import type { DialogDataConfig } from "@/_store/dialogs.module";
import { downloadFile } from "@/_helpers/download";
import { FilterContext, useFiltersStore } from "@/_store/filters.module";
import { DeviceAllowBlockListTypes } from "@/constants/devices";
import { StoreModule } from "@/constants/store.ts";

export interface DeviceAllowBlockListItem {
  value: string;
  description?: string;
  blocked?: boolean;
  type?: string;
  valueType?: string;
}

export interface DeviceAllowBlockListResponseItem {
  id: string;
  description?: string;
  inheritedFromParent: boolean;
  type: string;
  value: string;
  blocked: boolean;
  timestamp: number;
}

export interface DeviceAllowBlockListState {
  items: DeviceAllowBlockListResponseItem[];
  total: number;
  showSkeletonLoader: boolean;
  pagination: Pagination;
  loading: boolean;
}

export const defaultDeviceAllowBlockListState: DeviceAllowBlockListState = {
  items: [],
  total: 0,
  showSkeletonLoader: false,
  pagination: {
    page: 0,
    pageSize: 25,
  },
  loading: false,
};

export interface GetAllowBlocklistPayload {
  showSkeletonLoader?: boolean;
}

interface DeviceAllowBlockListStoreOptions {
  id: StoreModule.ENDPOINT_SECURITY_ALLOW_BLOCK_LIST | StoreModule.EDR_ALLOW_BLOCK_LIST;
  filterContext: FilterContext.DEVICE_ALLOW_BLOCK_LIST | FilterContext.EDR_ALLOW_BLOCK_LIST;
  isEdr: boolean;
}

const ENDPOINT_SECURITY_DEFAULT_TYPES = [
  DeviceAllowBlockListTypes.PROCESS,
  DeviceAllowBlockListTypes.FOLDER,
  DeviceAllowBlockListTypes.FILE,
] as const;

const EDR_DEFAULT_TYPES = [
  DeviceAllowBlockListTypes.EDR_PROCESS,
  DeviceAllowBlockListTypes.EDR_FOLDER,
  DeviceAllowBlockListTypes.EDR_FILE,
] as const;

const getDefaultTypesString = (isEdr: boolean) =>
  (isEdr ? EDR_DEFAULT_TYPES : ENDPOINT_SECURITY_DEFAULT_TYPES).join(",");

export const createDeviceAllowBlockListStore = (options: DeviceAllowBlockListStoreOptions) => {
  const defaultTypes = getDefaultTypesString(options.isEdr);

  return defineStore(options.id, {
    state: (): DeviceAllowBlockListState => ({
      ...defaultDeviceAllowBlockListState,
    }),
    actions: {
      setPagination(pagination: Pagination) {
        this.pagination = pagination;
      },
      resetPagination() {
        this.pagination = { ...defaultDeviceAllowBlockListState.pagination };
      },
      async getItems(
        { showSkeletonLoader = false }: GetAllowBlocklistPayload = { showSkeletonLoader: false }
      ) {
        this.showSkeletonLoader = showSkeletonLoader;
        this.loading = true;
        try {
          const filtersStore = useFiltersStore();
          const filters = filtersStore.filters[options.filterContext] as {
            type?: string | null;
            search?: string | null;
          };

          const requestPayload = api.getDeviceAllowBlocklist({
            type: filters?.type || defaultTypes,
            search: filters?.search,
            page: this.pagination.page,
            pageSize: this.pagination.pageSize,
          });

          const { data } = await axiosInstance.request(requestPayload);
          this.items = data.items;
          this.total = data.total;
        } catch (error) {
          console.error(error);
        } finally {
          this.loading = false;
          this.showSkeletonLoader = false;
        }
      },
      async addToAllowBlockList(payload: DialogDataConfig<DeviceAllowBlockListItem>) {
        const { value, description, blocked, type, valueType } = payload.item;
        const data: DeviceAllowBlockListItem = {
          value,
          description,
          blocked,
          type,
        };
        if (valueType) data.valueType = valueType;

        try {
          const request = {
            ...api.addDeviceAllowBlockList(data),
          };
          await axiosInstance.request(request);
          await this.getItems();
        } catch (error) {
          console.error(error);
        }
      },
      async importAllowBlockListFromCsv(file: File) {
        const data = new FormData();
        data.append("file", file);
        data.append("isEdrPage", String(options.isEdr));

        const request = {
          ...api.uploadEdrAllowBlockListCSV(data),
        };

        return axiosInstance.request(request);
      },
      async downloadAllowBlockListTemplate() {
        try {
          const request = {
            ...api.edrAllowBlockListCSVTemplate,
          };
          const { data, headers } = await axiosInstance.request(request);
          const blob = new Blob([data], { type: headers["content-type"] as string | undefined });
          const fileName = options.isEdr
            ? "EdrAllowBlockListExample.csv"
            : "EndpointAllowBlockListExample.csv";
          downloadFile(blob, fileName);
        } catch (error) {
          console.error(error);
        }
      },
      async removeFromAllowBlockList(payload: { item: DeviceAllowBlockListResponseItem }) {
        try {
          const request = {
            ...api.deleteDeviceAllowBlocklist(payload.item.id),
          };
          await axiosInstance.request(request);
          await this.getItems();
        } catch (error) {
          console.error(error);
        }
      },
    },
  });
};

export const useEndpointSecurityAllowBlockListStore = createDeviceAllowBlockListStore({
  id: StoreModule.ENDPOINT_SECURITY_ALLOW_BLOCK_LIST,
  filterContext: FilterContext.DEVICE_ALLOW_BLOCK_LIST,
  isEdr: false,
});

export const useEdrAllowBlockListStore = createDeviceAllowBlockListStore({
  id: StoreModule.EDR_ALLOW_BLOCK_LIST,
  filterContext: FilterContext.EDR_ALLOW_BLOCK_LIST,
  isEdr: true,
});
